import React from 'react';
import classes from '../SingingTab.module.scss';
import { Button, Table, Tree } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { prepareGroups } from '../../ArgeementTab/AgreementCard';
import { ISimpleGroupForAprove, ISimpleUserForAprove } from 'interfaces';
import { InviteStatusEnum } from 'interfaces/IInvitations';
import { iconCases, IIconCases } from '../../ArgeementTab/AgreementUsers';
import { EditOutlined, UserOutlined } from '@ant-design/icons';
import apiSigning from 'api/signing';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { fetchSigningState } from 'store/reducers/signingCreator';
import dayjs from 'dayjs';
import RowLoader from 'components/Sceletons/RowLoader';

type SigningParticipantsProps = {
  usersWithPermissions?: any[];
  loadingUsersPermissions?: boolean;
};

const SigningParticipants = ({ usersWithPermissions = [], loadingUsersPermissions = false }: SigningParticipantsProps) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [addMode, setAddMode] = React.useState(false);
  const [deleteMode, setDeleteMode] = React.useState(false);
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [isAddingParticipants, setIsAddingParticipants] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [usersId, setUsersId] = React.useState<string[]>([]);
  const [dataTreeSelect, setDataTreeSelect] = React.useState<any[]>([]);

  const { users, groups, isGroupsAndUsersLoading, pendingInviteUsers } = useAppSelector((store) => store.dataUsers);
  const { selectedDocument } = useAppSelector((state) => state.documents);
  const { messageApi } = useAppSelector((store) => store.windowState);
  const { signing, needSigningFromUsers, signingInfo } = useAppSelector((store) => store.signing);
  const { userData } = useAppSelector((store) => store.userData);

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const isInitiator = userData?.id === signingInfo?.initiator?.id;
  const locale = i18n.language || 'en';

  const handleCheckToAdd = (checkedKeys: any) => {
    const keysArray: string[] = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    const groupIds = new Set(groups.map((g) => g.id));
    const selectedUserIds = keysArray.filter((key) => !groupIds.has(key));
    setUsersId(selectedUserIds);
  };

  React.useEffect(() => {
    if (users.length && needSigningFromUsers.length) {
      setUsersId(needSigningFromUsers.map((user) => user.user_id));
    }
  }, [signing, users, needSigningFromUsers]);

  React.useEffect(() => {
    const usersWithSignStatus = users.map((user) => ({
      ...user,
      signing_status: needSigningFromUsers.find((signUser) => signUser.user_email === user.email)?.signing_status,
      document_permissions: usersWithPermissions.find((item) => item.email === user.email)?.document_permissions,
      type: 'user',
    }));
    const usersWithSignStatusIds = new Set(usersWithSignStatus.map(u => u.id));

    const invitesAsUsers = pendingInviteUsers
      .filter(invite => invite.invite_status === InviteStatusEnum.PENDING && !invite.is_parent_invite && !usersWithSignStatusIds.has(invite.id))
      .map((invite) => ({
        id: invite.user_id,
        first_name: '',
        last_name: '',
        email: invite.email,
        phone_number: '',
        group: [invite.group_id],
        type: 'invite',
      }));

    const allUsers = [...usersWithSignStatus, ...invitesAsUsers];
    const treeData = groups.map((group) => prepareGroups(group, allUsers, t, 'can_manage_signing'));
    setDataTreeSelect(treeData);
  }, [users, usersWithPermissions, pendingInviteUsers, locale, groups, needSigningFromUsers, t]);

  const handleToggleEditMode = () => {
    setIsEditMode((prev) => !prev);
    setAddMode(false);
    setDeleteMode(false);
    setSelectedUserIds([]);
  };

  const handleDeleteParticipants = async () => {
    if (!selectedUserIds.length || !signingInfo?.id || !selectedDocument?.id) return;
    setIsDeleting(true);
    try {
      await apiSigning.deleteSigningParticipants({
        document_id: selectedDocument.id,
        user_ids: selectedUserIds,
      });
      messageApi?.success(t('Documents.participants.deleteSuccess'));
      setSelectedUserIds([]);
      dispatch(fetchSigningState({ id: selectedDocument.id }));
    } catch {
      messageApi?.error(t('Documents.participants.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddParticipants = async () => {
    if (!usersId.length || !signingInfo?.id || !selectedDocument?.id) return;

    const allUsers = [
      ...users.map((user) => ({ ...user, type: 'user' })),
      ...pendingInviteUsers
        .filter(invite => invite.invite_status === InviteStatusEnum.PENDING)
        .map((invite) => ({
          id: invite.user_id,
          first_name: '',
          last_name: '',
          email: invite.email,
          phone_number: '',
          group: [invite.group_id],
          type: 'invite',
        })),
    ];

    const choosenUsers = allUsers
      .filter((user) => usersId.includes(user.id))
      .map((user) => ({
        user_id: user.id,
        user_name: user.type === 'invite' ? user.email : user.first_name,
        user_email: user.email,
        signing_status: null,
        parent_group_id: user.group[0],
      }));

    const simpleGroups: ISimpleGroupForAprove[] = groups.map((group) => ({ group_id: group.id, group_name: group.name }));

    const jsonForRequest = choosenUsers.reduce((acc: ISimpleGroupForAprove[], currentUser: ISimpleUserForAprove) => {
      const targetGroup = simpleGroups.find((group) => group.group_id === currentUser.parent_group_id);
      const theSameGroupInAcc = acc.find((group) => group.group_id === targetGroup?.group_id);

      delete currentUser.parent_group_id;

      theSameGroupInAcc
        ? (acc = acc.map((group: any) => (group.group_id === targetGroup?.group_id ? { ...group, users: [...group.users, currentUser] } : group)))
        : acc.push({ ...targetGroup!, users: [currentUser] });

      return acc;
    }, []);

    setIsAddingParticipants(true);
    try {
      await apiSigning.addSigningParticipants({ document_id: selectedDocument.id, signing_state: jsonForRequest });
      messageApi?.success(t('Documents.participants.addSuccess'));
      setAddMode(false);
      dispatch(fetchSigningState({ id: selectedDocument.id }));
    } catch {
      messageApi?.error(t('Documents.participants.addError'));
    } finally {
      setIsAddingParticipants(false);
    }
  };

  return (
    <div className={classes.participantsSection}>
      <div className={classes.tableTitleWrap}>
        <span className={classes.tableTitle}>
          {t('Documents.details.signing.signingState')}
        </span>
        {isInitiator && (
          <Button
            onClick={handleToggleEditMode}
            icon={<EditOutlined />}
            className={classes.liButton}
          >
            {isEditMode
              ? t('Documents.participants.cancel')
              : t('Documents.participants.edit')}
          </Button>
        )}
      </div>

      {signingInfo?.initiator && (
        <div className={classes.initiatorBlock}>
          <UserOutlined className={classes.initiatorIcon} />
          <div className={classes.initiatorInfo}>
            <span className={classes.initiatorName}>{signingInfo.initiator.name}</span>
            <span className={classes.initiatorEmail}>{signingInfo.initiator.email}</span>
          </div>
          <span className={classes.initiatorBadge}>{t('Documents.participants.initiator')}</span>
        </div>
      )}

      {isEditMode && (
        <div className={classes.editActions}>
          {!deleteMode && (
            <Button
              type="primary"
              className={classes.editButton}
              loading={isAddingParticipants}
              disabled={addMode && !usersId.length}
              onClick={() => {
                if (addMode) {
                  handleAddParticipants();
                } else {
                  setAddMode(true);
                  setDeleteMode(false);
                }
              }}
            >
              {t('Documents.participants.addUser')}
            </Button>
          )}
          {!addMode && (
            <Button
              variant="solid"
              color="danger"
              className={classes.editButton}
              disabled={deleteMode && !selectedUserIds.length}
              loading={isDeleting}
              onClick={() => {
                if (deleteMode) {
                  handleDeleteParticipants();
                } else {
                  setDeleteMode(true);
                  setAddMode(false);
                }
              }}
            >
              {t('Documents.participants.deleteUser')}
            </Button>
          )}
        </div>
      )}

      {addMode && (
        isGroupsAndUsersLoading || loadingUsersPermissions ? (
          <RowLoader height={180} width={250} padding={'0 0 0 0'} />
        ) : (
          <Tree
            checkable
            disabled={isAddingParticipants}
            treeData={dataTreeSelect}
            checkedKeys={usersId}
            onCheck={handleCheckToAdd}
            className={classes.addParticipantsTree}
          />
        )
      )}

      {!addMode && (
        <Table
          size='small'
          pagination={false}
          rowKey='user_id'
          rowSelection={deleteMode ? {
            selectedRowKeys: selectedUserIds,
            onChange: (keys) => setSelectedUserIds(keys as string[]),
            getCheckboxProps: (record: ISimpleUserForAprove) => ({
              disabled: record.signing_status !== null && record.signing_status !== undefined,
            }),
          } : undefined}
          dataSource={needSigningFromUsers}
          columns={[
            {
              title: t('Documents.details.signing.userName'),
              dataIndex: 'user_email',
              key: 'user_email',
              width: '50%',
            },
            {
              title: t('Documents.details.signing.date'),
              dataIndex: 'signing_date',
              key: 'signing_date',
              width: '30%',
              render: (val: string) => val
                ? getDateWithTimeZone(dayjs(val).valueOf(), userData?.date_format!)
                : '-',
            },
            {
              title: t('Documents.details.signing.state'),
              dataIndex: 'signing_status',
              key: 'signing_status',
              width: '20%',
              render: (val: boolean | null) => iconCases(`${val}` as IIconCases),
            },
          ]}
        />
      )}
    </div>
  );
};

export default SigningParticipants;
